var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment, h } from 'preact';
import { useCallback, useEffect, useId, useRef, useState, } from 'preact/hooks';
import './FittingRoomPopup.scss';
import { getInstoreModeFromStorage, } from './fittingRoomUtils';
const defaultLabels = {
    heading: 'Great choice!',
    nameLabel: 'Please enter your name',
    nameHint: '(so we know this item is for you)',
    fetching: "We're fetching your item now!",
    continueShopping: 'CONTINUE SHOPPING',
    addToFittingRoom: 'ADD TO CHANGEROOM',
    addToNestLounge: 'ADD TO STYLING ROOM',
    errorNameRequired: 'Please enter your name',
    errorSkuMissing: 'Product SKU is missing. Please try again.',
    errorHandleMissing: 'Product handle is missing. Please try again.',
    errorSaveFailed: 'Failed to save. Please try again.',
    successFittingRoom: 'This was added to your Changeroom',
    successNestLounge: 'This was added to your Styling Room',
    closeAria: 'Close',
};
function formatMoneyDollars(amount) {
    return `$${amount.toFixed(2)}`;
}
function productShowsSizeOption(product) {
    var _a, _b, _c, _d;
    const variantCount = (_b = (_a = product.variants) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    const sizes = (_d = (_c = product.size) === null || _c === void 0 ? void 0 : _c.filter(Boolean)) !== null && _d !== void 0 ? _d : [];
    return variantCount > 1 || sizes.length > 1 || sizes.length === 1;
}
function variantSizeDisplayLine(variant) {
    var _a, _b, _c;
    if (!variant)
        return '';
    const opt = (_a = variant.selectedOptions) === null || _a === void 0 ? void 0 : _a.find((o) => o.name === 'Size' || o.name === 'Title');
    return ((_c = (_b = opt === null || opt === void 0 ? void 0 : opt.value) !== null && _b !== void 0 ? _b : variant.title) !== null && _c !== void 0 ? _c : '').trim();
}
function skuForFittingRoomRequest(variant, product) {
    var _a, _b;
    return ((_b = (_a = variant === null || variant === void 0 ? void 0 : variant.sku) !== null && _a !== void 0 ? _a : product.sku) !== null && _b !== void 0 ? _b : '').trim();
}
function CloseIcon() {
    return (h("svg", { "aria-hidden": true, focusable: "false", role: "presentation", className: "icon icon-close", viewBox: "0 0 192 192" },
        h("path", { d: "M150 42 42 150M150 150 42 42", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "8", fill: "none" })));
}
export function FittingRoomPopup({ open, onClose, product, variant, heroImageUrl, heroImageAlt, colorLabel, displayPriceRegular, displayPriceSale, instoreMode: instoreModeProp, labels: labelsProp, className, }) {
    var _a, _b, _c, _d, _e, _f;
    const labels = Object.assign(Object.assign({}, defaultLabels), labelsProp);
    const headingId = useId();
    const nameInputId = useId();
    const errorBoxRef = useRef(null);
    const [name, setName] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('fitting_room_buyer_name') || '';
        }
        return '';
    });
    const [loading, setLoading] = useState(false);
    const [isAdded, setIsAdded] = useState(false);
    const [feedback, setFeedback] = useState({ type: 'hidden' });
    const [instoreMode, setInstoreMode] = useState(null);
    useEffect(() => {
        if (instoreModeProp !== undefined) {
            setInstoreMode(instoreModeProp);
            return;
        }
        setInstoreMode(getInstoreModeFromStorage());
    }, [instoreModeProp, open]);
    useEffect(() => {
        if (open) {
            if (typeof window !== 'undefined') {
                const cachedName = localStorage.getItem('fitting_room_buyer_name');
                if (cachedName && !name) {
                    setName(cachedName);
                }
            }
        }
        else {
            setFeedback({ type: 'hidden' });
            setLoading(false);
            setIsAdded(false);
        }
    }, [open]);
    const hideFeedback = useCallback(() => {
        setFeedback({ type: 'hidden' });
    }, []);
    const showError = useCallback((message) => {
        setFeedback({ type: 'error', message });
        queueMicrotask(() => {
            var _a;
            (_a = errorBoxRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
    }, []);
    const handleSave = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (isAdded)
            return;
        hideFeedback();
        const buyerName = name.trim();
        if (!buyerName) {
            showError(labels.errorNameRequired);
            return;
        }
        const sku = skuForFittingRoomRequest(variant, product);
        if (!sku) {
            showError(labels.errorSkuMissing);
            return;
        }
        const handle = (_b = (_a = product.handle) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : '';
        if (!handle) {
            showError(labels.errorHandleMissing);
            return;
        }
        const mode = instoreModeProp !== null && instoreModeProp !== void 0 ? instoreModeProp : instoreMode;
        const fittingRoomId = mode === 'on' ? 'Fitting Room' : 'Nest Lounge';
        setLoading(true);
        try {
            const payload = {
                buyer_name: buyerName,
                fitting_room_id: fittingRoomId,
                lines: [{ sku, qty: 1, handle }],
            };
            const fittingRoomApiUrl = `${process.env.apiBaseUrl}public/v1/fitting-room/order`;
            const res = yield fetch(fittingRoomApiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            yield res.json();
            if (typeof window !== 'undefined' && buyerName) {
                localStorage.setItem('fitting_room_buyer_name', buyerName);
            }
            setIsAdded(true);
        }
        catch (e) {
            console.error('Error saving to fitting room:', e);
            showError(labels.errorSaveFailed);
        }
        finally {
            setLoading(false);
        }
    });
    const resolvedHeroUrl = (heroImageUrl === null || heroImageUrl === void 0 ? void 0 : heroImageUrl.trim()) ||
        (typeof product.image === 'string'
            ? product.image
            : ((_b = (_a = product.image) === null || _a === void 0 ? void 0 : _a.url) !== null && _b !== void 0 ? _b : ''));
    const resolvedHeroAlt = (heroImageAlt === null || heroImageAlt === void 0 ? void 0 : heroImageAlt.trim()) ||
        (typeof product.image === 'object' && ((_c = product.image) === null || _c === void 0 ? void 0 : _c.alt)
            ? product.image.alt
            : product.title);
    const colourLine = ((colorLabel === null || colorLabel === void 0 ? void 0 : colorLabel.trim()) || ((_d = product.color) === null || _d === void 0 ? void 0 : _d.trim()) || '').trim();
    const showSizeOption = productShowsSizeOption(product);
    const priceBlock = displayPriceRegular != null && displayPriceRegular >= 0
        ? renderPriceBlockFromCardDisplay(displayPriceRegular, displayPriceSale)
        : renderPriceBlockFromVariant(variant);
    const optionLine = variantSizeDisplayLine(variant);
    const saveButtonText = isAdded
        ? instoreMode === 'styling'
            ? labels.successNestLounge
            : labels.successFittingRoom
        : instoreMode === 'styling'
            ? labels.addToNestLounge
            : labels.addToFittingRoom;
    if (!open)
        return null;
    const overlayClass = ['fr-modal-overlay', className]
        .filter(Boolean)
        .join(' ');
    return (h("div", { className: overlayClass, role: "presentation", onMouseDown: (e) => {
            if (e.target === e.currentTarget)
                onClose();
        } },
        h("div", { className: "fr-popup-quick-view popup-quick-view", role: "dialog", "aria-modal": true, "aria-labelledby": headingId, onMouseDown: (e) => e.stopPropagation() },
            h("div", { className: "fr-popup-quick-view__inner" },
                h("div", { className: "fr-popup-quick-view__form popup-quick-view__form popup-quick-view__form--small" },
                    h("div", { className: "fr-popup-quick-view__wrapper popup-quick-view__wrapper" },
                        h("button", { type: "button", title: labels.closeAria, className: "fr-popup-quick-view__close popup-quick-view__close", onClick: onClose },
                            h(CloseIcon, null)),
                        h("div", { className: "fr-popup-quick-view__body popup-quick-view__body" },
                            h("div", { className: "fr-popup-quick-view__body__inner popup-quick-view__body__inner" },
                                h("h2", { className: "fitting-room-popup__heading", id: headingId }, labels.heading),
                                h("div", { className: "fitting-room-popup__image-column" }, resolvedHeroUrl ? (h("div", { className: "fr-product-gallery__media product-gallery__media product-gallery__media--image" },
                                    h("img", { src: resolvedHeroUrl, alt: resolvedHeroAlt }))) : (h("div", { className: "fr-product-gallery__media fr-product-gallery__media--placeholder product-gallery__media product-gallery__media--image" }, "No image"))),
                                h("div", { className: "fr-popup-quick-view__content popup-quick-view__content fitting-room-popup__content-column" },
                                    h("div", { className: "fitting-room-popup__product-details", "data-fitting-room-product": true, "data-variant-id": (_e = variant === null || variant === void 0 ? void 0 : variant.id) !== null && _e !== void 0 ? _e : '', "data-variant-sku": (_f = variant === null || variant === void 0 ? void 0 : variant.sku) !== null && _f !== void 0 ? _f : '', "data-product-handle": product.handle },
                                        h("h3", { className: "fitting-room-popup__product-title" }, product.title),
                                        h("div", { className: "fitting-room-popup__product-price fitting-room-popup__product-price" }, priceBlock),
                                        showSizeOption ? (h("div", { className: "fitting-room-popup__product-option" },
                                            h("span", { className: "fitting-room-popup__option-label" }, "SIZE:"),
                                            h("span", { className: "fitting-room-popup__option-value" }, optionLine))) : null,
                                        colourLine ? (h("div", { className: "fitting-room-popup__product-option" },
                                            h("span", { className: "fitting-room-popup__option-label" }, "COLOUR:"),
                                            h("span", { className: "fitting-room-popup__option-value" }, colourLine.toUpperCase()))) : null),
                                    h("div", { className: "fitting-room-popup__form" },
                                        h("label", { className: "fitting-room-popup__form-label", htmlFor: nameInputId }, labels.nameLabel),
                                        h("p", { className: "fitting-room-popup__form-hint" }, labels.nameHint),
                                        h("input", { id: nameInputId, type: "text", name: "fitting-room-name", className: "fitting-room-popup__form-input", value: name, onChange: (e) => setName(e.currentTarget.value), autoComplete: "name" }),
                                        h("p", { className: "fitting-room-popup__fetching" },
                                            h("em", null, labels.fetching))),
                                    h("div", { className: "fr-product__submit__holder product__submit__holder" },
                                        h("div", { className: "fr-product__submit__buttons product__submit__buttons" },
                                            h("div", { className: "fr-product__submit__item product__submit__item" },
                                                h("button", { type: "button", className: `fr-btn fitting-room-popup__btn fitting-room-popup__btn--save btn btn--advanced ${loading ? 'is-loading' : ''}`, disabled: loading || isAdded, onClick: handleSave },
                                                    h("span", null, saveButtonText),
                                                    h("em", { className: "fr-loader--line loader loader--line" },
                                                        h("em", { className: "fr-loader-indeterminate loader-indeterminate" })))),
                                            h("div", { className: "fr-product__submit__item product__submit__item" },
                                                h("button", { type: "button", className: "fr-btn fr-btn--continue fitting-room-popup__btn fitting-room-popup__btn--continue btn btn--advanced", onClick: onClose },
                                                    h("span", null, labels.continueShopping)))),
                                        h("div", { ref: errorBoxRef, role: "alert", className: `fr-product__form__errors product__form__errors ${feedback.type !== 'hidden' ? 'is-visible' : ''}` }, feedback.type === 'error' ? (h("div", { className: "errors", "data-error": true, tabIndex: -1 },
                                            feedback.message,
                                            h("button", { type: "button", className: "errors__close", "aria-label": labels.closeAria, onClick: hideFeedback },
                                                h(CloseIcon, null)))) : null)))))))))));
}
/** Same sale logic as BirdsnestProductCard `isSale` */
function renderPriceBlockFromCardDisplay(displayRegular, displaySale) {
    const isSale = displaySale != null &&
        displaySale >= 0 &&
        displaySale < displayRegular;
    if (!displayRegular) {
        return (h("span", { "data-product-price": true, className: "product__price--regular product__price--sale" }, "Free"));
    }
    if (isSale && displaySale != null) {
        return (h(Fragment, null,
            h("s", { "data-compare-price": true, className: "product__price--compare" }, displayRegular),
            ' ',
            h("span", { "data-product-price": true, className: "product__price--regular product__price--sale" }, displaySale)));
    }
    return (h("span", { "data-product-price": true, className: "product__price--regular product__price--sale" }, displayRegular));
}
function renderPriceBlockFromVariant(variant) {
    var _a, _b, _c, _d, _e;
    if (!(variant === null || variant === void 0 ? void 0 : variant.price))
        return null;
    const regularD = (_a = variant.price.regular) !== null && _a !== void 0 ? _a : 0;
    const saleD = variant.price.sale;
    const displayD = saleD != null && saleD < regularD ? saleD : regularD;
    const compareD = (_e = (_c = (_b = variant.compareAtPrice) === null || _b === void 0 ? void 0 : _b.regular) !== null && _c !== void 0 ? _c : (_d = variant.compareAtPrice) === null || _d === void 0 ? void 0 : _d.sale) !== null && _e !== void 0 ? _e : null;
    const formattedPrice = formatMoneyDollars(displayD);
    if (compareD != null && compareD > displayD) {
        const formattedCompare = formatMoneyDollars(compareD);
        return (h(Fragment, null,
            h("span", { "data-product-price": true, className: "product__price--regular product__price--sale" }, formattedPrice),
            ' ',
            h("s", { "data-compare-price": true, className: "product__price--compare" }, formattedCompare)));
    }
    return (h("span", { "data-product-price": true, className: "product__price--regular product__price--sale" }, formattedPrice));
}
